// The accepted tolerance on a numerical question.
//
// STORED AS A FRACTION, SHOWN AS A PERCENTAGE. The backend grades with
// `lower = (1 - tolerance) * answer` and `upper = (1 + tolerance) * answer`, so
// the stored 0.05 genuinely means "within 5% of the answer" — it was only ever
// displayed in the wrong unit. Everything here is presentation: nothing changes
// what is stored or how anything is graded.

export const DEFAULT_TOLERANCE_FRACTION = 0.05;

// Rounding exists because the conversion is not exact in binary floating point:
// 0.0333 * 100 is 3.3300000000000005, which would show as a nonsense percentage
// and, worse, would be written back as a slightly different stored value on
// every save. Six places is far beyond any real tolerance and makes the
// round-trip lossless for every value in use.
const PERCENT_PRECISION = 1e6;
const FRACTION_PRECISION = 1e12;

/** Fraction as stored (0.05) to percentage as displayed (5). */
export function toleranceToPercent(fraction: number): number {
  return Math.round(fraction * 100 * PERCENT_PRECISION) / PERCENT_PRECISION;
}

/** Percentage as entered (5) to the fraction that gets stored (0.05). */
export function percentToTolerance(percent: number): number {
  return Math.round((percent / 100) * FRACTION_PRECISION) / FRACTION_PRECISION;
}

/** The stored fraction rendered for display: 0.05 -> "5%". */
export function formatTolerancePercent(fraction: number): string {
  return `${toleranceToPercent(fraction)}%`;
}

/**
 * The tolerance band around an answer, for explaining the rule concretely.
 * Returns undefined when there is no answer to anchor it to.
 */
export function describeToleranceRange(
  fraction: number,
  answer: number | undefined,
): string | undefined {
  if (answer === undefined || !Number.isFinite(answer)) {
    return undefined;
  }

  const spread = Math.abs(answer * fraction);
  const lower = answer - spread;
  const upper = answer + spread;
  // Trim the floating point noise that multiplication leaves behind, without
  // forcing a fixed number of decimals onto a value that does not need them.
  const format = (value: number) => String(Number(value.toPrecision(12)));

  return `${format(lower)} to ${format(upper)}`;
}

// Written for the reader in each case. The rule is the same; a student needs to
// know their answer does not have to be exact, and an instructor needs to know
// what they are setting.
export const STUDENT_TOLERANCE_HELP =
  'Your answer counts as correct if it falls within this percentage of the exact value, so it does not have to match exactly.';

export const INSTRUCTOR_TOLERANCE_HELP =
  'How far from the correct answer a response can be and still earn full credit. At 5%, an answer of 20 accepts anything from 19 to 21.';
